import * as CertificationWatermark from '@tableau/certification-watermark';
import { SiteUrls } from '@tableau/shared-urls';
import * as React from 'react';
import * as _ from 'underscore';

import { EmbeddablePublishedDatasourceDataQualityWarning } from '@tableau/data-catalog-client-components';
import { IUser } from '@tableau/vizportal-webclient-api/lib/api/types/IUser';
import { catalogApolloClient } from '../../../catalog/CatalogApolloClient';
import { Messages } from '../../../codegen/Localize';
import { CommonStyles } from '../../../common/react/CommonStyles';
import { imgPath } from '../../../common/utilities/ImgUrl';
import { FlowLinksWithIcon } from '../../../flow/components/FlowLinksWithIcon';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { UserMapper } from '../../../ts/mappers/UserMapper';
import { FlowListItemInfo } from '../../../ts/models/FlowInfo';
import { ISpaceItemDetailInfo } from '../../../ts/models/ISpaceItemDetailInfo';
import { IUserInfo } from '../../../ts/models/IUserInfo';
import { IDatasourceActions } from '../../../ts/ServerApi/types/IDatasourceActions';
import { dataQualityIndicatorInfoToProps } from '../../quality/DataQualityType';
import { SiteUrlsProps } from '../../selectors/SiteUrlsSelectors';
import { NewWorkbookEntityAction } from '../ActionsMenu/EntityActions/NewWorkbookEntityAction';
import { ExplorerStyles } from '../Explorer/ExplorerStyles';
import { ConnectedExtractMetadata } from '../Header/ExtractMetadata';
import { ConnectedExtractTasksMetadata } from '../Header/ExtractTasksMetadata';
import { ItemDescription } from '../Header/HeaderItemDescription';
import { CertificationMetadata, OwnerMetadata } from '../Header/HeaderMetadataRow';
import { ActionsMenuIcon, DetailButtonIcon, FavoriteToggleWithCountsIcon } from '../Header/HeaderNameRowIcons';
import { ConnectedSpaceHeader, SpaceHeaderTestIds } from '../Header/SpaceHeader';
import { SpaceHeaderStyles } from '../Header/SpaceHeaderStyles';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { HiddenUserStyles } from '../SpacesStyles';
import { createContentButton } from '../SubspaceHelpers';


const DATASOURCE_CREATE_CONTENT_FLYOUT_WIDTH: number = 260;

export interface DatasourceSpaceHeaderProps extends SiteUrlsProps {
  readonly item: ISpaceItemDetailInfo<'datasource'>;
  readonly availableActions: IDatasourceActions;
  readonly flows: ReadonlyArray<FlowListItemInfo>;
  readonly openDetail: () => void;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
  readonly sanitizer: ng.sanitize.ISanitizeService;
  readonly catalogingOnSiteEnabled: boolean;
}

export function DatasourceSpaceHeader(props: DatasourceSpaceHeaderProps): JSX.Element {
  const spotlightActions = FeatureFlags.MaestroFlowEditingServerV1 ? undefined : [ NewWorkbookEntityAction ];
  return <>
    <ConnectedSpaceHeader
      item={props.item}
      availableActions={props.availableActions}
      nameRowIcons={
        <React.Fragment>
          { FavoriteToggleWithCountsIcon({
            item: props.item,
            addToFavorites: props.addToFavorites,
            removeFromFavorites: props.removeFromFavorites,
          }) }
          { DetailButtonIcon(props.item, props.openDetail) }
          { ActionsMenuIcon(props.item) }
        </React.Fragment>
      }
      metadataRow={ datasourceMetadataRow(props.item, props.flows, props.urls, props.availableActions, props.catalogingOnSiteEnabled) }
      descriptionElement={ ItemDescription(props.item, props.openDetail, props.sanitizer) }
      spotlightActions={spotlightActions}
    />
    {FeatureFlags.MaestroFlowEditingServerV1 ? <div className={`${ExplorerStyles.ToolbarButtonGroup} ${SpaceHeaderStyles.SpotlightActionButtonsRow}`} style={{fontWeight: 'normal',}}>
      {createContentButton(props.item.project.id, props.item.contentType, DATASOURCE_CREATE_CONTENT_FLYOUT_WIDTH)}
    </div> : null}
  </>;
}

export function datasourceMetadataRow(
    datasourceInfo: ISpaceItemDetailInfo<'datasource'>,
    flows: ReadonlyArray<FlowListItemInfo>,
    urls: SpacesSiteUrls,
    availableActions: IDatasourceActions,
    catalogingOnSiteEnabled: boolean
): JSX.Element {
  return <React.Fragment>
    { OwnerMetadata(datasourceInfo.owner, urls) }
    { CertificationMetadata(datasourceInfo, urls) }
    <ConnectedExtractMetadata itemInfo={datasourceInfo} availableActions={availableActions} />
    <ConnectedExtractTasksMetadata itemInfo={datasourceInfo} availableActions={availableActions} />
    { PublishedByFlowMetadata(flows, urls) }
    { catalogingOnSiteEnabled && <DataQualityMetadata datasourceInfo={datasourceInfo} urls={urls} /> }
  </React.Fragment>;
}

function PublishedByFlowMetadata(flows: ReadonlyArray<FlowListItemInfo>, urls: SiteUrls): JSX.Element | null{
  if (flows.length > 0) {
    return (
      <>
        <span className={SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin}>
          {Messages.datasourceHeaderPublishedByFlow()}
        </span>
        <span className={SpaceHeaderStyles.MetadataInfo}>
          <FlowLinksWithIcon flows={flows} hideIcon={true} siteUrls={urls} />
        </span>
      </>
    );
  } else {
    return null;
  }
}

function DataQualityMetadata(props: { datasourceInfo: ISpaceItemDetailInfo<'datasource'>, urls: SiteUrls }): JSX.Element | null {
  const first = _.first(props.datasourceInfo.dataQualityIndicators);
  const own = dataQualityIndicatorInfoToProps({
    assetUrl: props.urls.datasourceUrl(props.datasourceInfo.id),
    assetName: props.datasourceInfo.name,
    assetType: 'PublishedDatasource',
    onContentItem: true
  }, props.urls, first);

  return (
    <span className={SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin}>
      <EmbeddablePublishedDatasourceDataQualityWarning
        client={catalogApolloClient}
        vizportalUrlId={props.datasourceInfo.id}
        ownContentWarning={own}
        siteUrls={props.urls}
      />
    </span>
  );
}
